/* Nether: one express wallet under Checkout, the one this device is for
   (2026-09-25). Loaded by the bag (snippets/cart-drawer.liquid) and by the
   cart page (sections/main-cart.liquid), because Horizon does not render the
   bag on /cart at all.

   Owner: "on an iPhone, I get to see the Apple pay button. If I'm on an
   Android, it's Google pay. And then otherwise it'd be just how the shop
   button works." Shopify's cart slot builds a button for every wallet switched
   on in Payments (Shop Pay, Apple Pay, PayPal, Google Pay). Four do not fit a
   row on a phone, so they stacked full width over the bag. The product page's
   dynamic button already shows one wallet per device; the cart has no option
   for it.

   So nether-cart-summary keeps Shopify's element inert in a <template>, and it
   is mounted from here with the list it builds from (its wallet-configs
   attribute) narrowed to one wallet before it ever renders. It is still
   Shopify's element, its button and its payment flow, told about one wallet.

   Which one: Apple Pay wherever this browser says it can pay with Apple Pay,
   Google Pay in Android's own browsers, Shop Pay everywhere else. If the
   chosen wallet is not on Shopify's list, Shop Pay. If the list cannot be read
   at all, nothing mounts and the bag ends on Checkout securely and the badges,
   the way it did before any of this. Names are compared letters-only
   ("apple_pay", "applePay" and "Apple Pay" are one wallet), because this is
   Shopify's markup and its spelling is not ours to rely on.

   IN-APP BROWSERS (owner, same night: "what can be done about from Instagram
   or Facebook, can that not show up there?"). Apple Pay is asked, not
   assumed. iOS 13 to 15 switched Apple Pay off in any in-app browser that
   injected its own scripts, which Instagram and Facebook do; iOS 16 lifted
   that. So whether it works inside Instagram is the app's call, and
   ApplePaySession.canMakePayments() is the browser answering for itself: if it
   says yes, Instagram's own browser gets Apple Pay; if it says no or refuses
   to answer, Shop Pay, which works in every in-app browser. Android's in-app
   browsers stay on Shop Pay: Google Pay inside an app's web view needs the app
   to switch it on, and a page cannot tell reliably whether it has, so asking
   for it there risks a bag with no button. */

const WALLET_IN_APP = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|TikTok|musical_ly|Snapchat|Pinterest/i;

function walletKey(name) {
  return String(name || '').toLowerCase().replace(/[^a-z]/g, '');
}

// Remembered for the visit once a wallet failed to draw here (see below), so
// later bags go straight to Shop Pay instead of waiting for the same failure.
const WALLET_FAILED_KEY = 'nether-wallet-fell-back';

function walletFellBack() {
  try {
    return window.sessionStorage.getItem(WALLET_FAILED_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function walletForDevice() {
  const ua = navigator.userAgent || '';
  if (walletFellBack()) return 'shoppay';
  try {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) return 'applepay';
  } catch (error) {
    // Apple Pay refuses to answer in some embedded or insecure contexts.
  }
  if (/Android/i.test(ua) && !WALLET_IN_APP.test(ua)) return 'googlepay';
  return 'shoppay';
}

/* A browser can say yes to Apple Pay and Shopify can still decline to draw it
   (owner, same night, from an in-app browser: "a bunch of padding underneath
   the button... Like the PayPal button is hidden", which was an empty wallet
   box under Checkout). So a wallet that has not drawn a button a few seconds
   after mounting is swapped for Shop Pay, which draws everywhere. Checked by
   the wallet's element existing, not by its height, so a bag closed in the
   meantime is not mistaken for a failure. Shop Pay itself is not rechecked. */
const WALLET_DRAW_WAIT_MS = 5000;

function mountWallet(box, fallback) {
  if (box.querySelector('shopify-accelerated-checkout-cart')) return;
  const source = box.querySelector('template[data-nether-wallets-source]');
  if (!(source instanceof HTMLTemplateElement)) return;
  const fragment = source.content.cloneNode(true);
  const element = fragment.querySelector('shopify-accelerated-checkout-cart');
  if (!element) return;

  let wallets;
  try {
    wallets = JSON.parse(element.getAttribute('wallet-configs') || '');
  } catch (error) {
    return;
  }
  if (!Array.isArray(wallets)) return;

  const want = fallback ? 'shoppay' : walletForDevice();
  const pick =
    wallets.find((wallet) => wallet && walletKey(wallet.name) === want) ||
    wallets.find((wallet) => wallet && walletKey(wallet.name) === 'shoppay');
  if (!pick) return;

  element.setAttribute('wallet-configs', JSON.stringify([pick]));
  box.appendChild(fragment);

  if (walletKey(pick.name) === 'shoppay') return;
  setTimeout(() => {
    if (!element.isConnected || element.children.length) return;
    // Nothing drawn: take the whole mount out (its wrapper carries an id that
    // must stay single on the page) and put Shop Pay in its place.
    const wrapper = element.closest('.dynamic-checkout__content');
    (wrapper && box.contains(wrapper) ? wrapper : element).remove();
    try {
      window.sessionStorage.setItem(WALLET_FAILED_KEY, '1');
    } catch (error) {
      // No storage (private mode, blocked): this bag still falls back.
    }
    mountWallet(box, true);
  }, WALLET_DRAW_WAIT_MS);
}

function mountWallets() {
  document.querySelectorAll('[data-nether-wallets]').forEach(mountWallet);
}

// The bag's summary arrives and is replaced by section renders, so watch the
// page and mount whatever turns up, once a frame at most. A module runs once
// per page however many times its tag appears, so this is registered once.
let walletFrame = 0;
new MutationObserver(() => {
  if (walletFrame) return;
  walletFrame = requestAnimationFrame(() => {
    walletFrame = 0;
    mountWallets();
  });
}).observe(document.body, { childList: true, subtree: true });
mountWallets();
